import { useState, useEffect, useCallback } from "react";
import {
  getOutreachItems,
  addOutreachItem,
  updateOutreachItem,
  deleteOutreachItem,
} from "../services/outreach/firestore/crud";
import {
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  MusicIndustry,
  Campaign,
  OutreachCategory,
} from "../types/outreach";

type OutreachItem =
  | ReactionChannel
  | PlaylistingChannel
  | SpotifyPlaylist
  | MusicBlog
  | MusicIndustry
  | Campaign;

interface UseOutreachDataReturn {
  reactionChannels: ReactionChannel[];
  playlistingChannels: PlaylistingChannel[];
  spotifyPlaylists: SpotifyPlaylist[];
  musicBlogs: MusicBlog[];
  campaigns: Campaign[];
  loading: boolean;
  error: Error | null;
  addItem: <T extends OutreachItem>(
    category: OutreachCategory,
    item: Omit<T, "id">
  ) => Promise<string>;
  updateItem: <T extends OutreachItem>(
    category: OutreachCategory,
    id: string,
    item: Partial<T>
  ) => Promise<void>;
  deleteItem: (category: OutreachCategory, id: string) => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useOutreachData = (): UseOutreachDataReturn => {
  const [reactionChannels, setReactionChannels] = useState<ReactionChannel[]>(
    []
  );
  const [playlistingChannels, setPlaylistingChannels] = useState<
    PlaylistingChannel[]
  >([]);
  const [spotifyPlaylists, setSpotifyPlaylists] = useState<SpotifyPlaylist[]>(
    []
  );
  const [musicBlogs, setMusicBlogs] = useState<MusicBlog[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reactionData = await getOutreachItems<ReactionChannel>(
        OutreachCategory.REACTION_CHANNELS
      );
      setReactionChannels(reactionData);

      const playlistingData = await getOutreachItems<PlaylistingChannel>(
        OutreachCategory.PLAYLISTING_CHANNELS
      );
      setPlaylistingChannels(playlistingData);

      const spotifyData = await getOutreachItems<SpotifyPlaylist>(
        OutreachCategory.SPOTIFY_PLAYLISTS
      );
      setSpotifyPlaylists(spotifyData);

      const blogsData = await getOutreachItems<MusicBlog>(
        OutreachCategory.MUSIC_BLOGS
      );
      setMusicBlogs(blogsData);

      const campaignsData = await getOutreachItems<Campaign>(
        OutreachCategory.CAMPAIGNS
      );
      setCampaigns(campaignsData);
    } catch (err) {
      console.error("Error loading outreach data:", err);
      setError(
        err instanceof Error ? err : new Error("Unknown error loading data")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addItem = async <T extends OutreachItem>(
    category: OutreachCategory,
    item: Omit<T, "id">
  ): Promise<string> => {
    try {
      const newItemId = await addOutreachItem(category, item);
      const newItem = { ...item, id: newItemId } as T;

      // Update state based on category
      switch (category) {
        case OutreachCategory.REACTION_CHANNELS:
          setReactionChannels((prev) => [...prev, newItem as ReactionChannel]);
          break;
        case OutreachCategory.PLAYLISTING_CHANNELS:
          setPlaylistingChannels((prev) => [
            ...prev,
            newItem as PlaylistingChannel,
          ]);
          break;
        case OutreachCategory.SPOTIFY_PLAYLISTS:
          setSpotifyPlaylists((prev) => [...prev, newItem as SpotifyPlaylist]);
          break;
        case OutreachCategory.MUSIC_BLOGS:
          setMusicBlogs((prev) => [...prev, newItem as MusicBlog]);
          break;
        case OutreachCategory.CAMPAIGNS:
          setCampaigns((prev) => [...prev, newItem as Campaign]);
          break;
      }

      return newItemId;
    } catch (err) {
      console.error("Error adding item:", err);
      setError(
        err instanceof Error ? err : new Error("Unknown error adding item")
      );
      throw err;
    }
  };

  const updateItem = async <T extends OutreachItem>(
    category: OutreachCategory,
    id: string,
    item: Partial<T>
  ): Promise<void> => {
    try {
      await updateOutreachItem(category, id, item);

      // Update state based on category
      const updateState = (prevItems: any[]) =>
        prevItems.map((prevItem) =>
          prevItem.id === id ? { ...prevItem, ...item } : prevItem
        );

      switch (category) {
        case OutreachCategory.REACTION_CHANNELS:
          setReactionChannels(updateState);
          break;
        case OutreachCategory.PLAYLISTING_CHANNELS:
          setPlaylistingChannels(updateState);
          break;
        case OutreachCategory.SPOTIFY_PLAYLISTS:
          setSpotifyPlaylists(updateState);
          break;
        case OutreachCategory.MUSIC_BLOGS:
          setMusicBlogs(updateState);
          break;
        case OutreachCategory.CAMPAIGNS:
          setCampaigns(updateState);
          break;
      }
    } catch (err) {
      console.error("Error updating item:", err);
      setError(
        err instanceof Error ? err : new Error("Unknown error updating item")
      );
      throw err;
    }
  };

  const deleteItem = async (
    category: OutreachCategory,
    id: string
  ): Promise<void> => {
    try {
      await deleteOutreachItem(category, id);

      // Update state based on category
      const filterItems = (items: any[]) =>
        items.filter((item) => item.id !== id);

      switch (category) {
        case OutreachCategory.REACTION_CHANNELS:
          setReactionChannels(filterItems);
          break;
        case OutreachCategory.PLAYLISTING_CHANNELS:
          setPlaylistingChannels(filterItems);
          break;
        case OutreachCategory.SPOTIFY_PLAYLISTS:
          setSpotifyPlaylists(filterItems);
          break;
        case OutreachCategory.MUSIC_BLOGS:
          setMusicBlogs(filterItems);
          break;
        case OutreachCategory.CAMPAIGNS:
          setCampaigns(filterItems);
          break;
      }
    } catch (err) {
      console.error("Error deleting item:", err);
      setError(
        err instanceof Error ? err : new Error("Unknown error deleting item")
      );
      throw err;
    }
  };

  return {
    reactionChannels,
    playlistingChannels,
    spotifyPlaylists,
    musicBlogs,
    campaigns,
    loading,
    error,
    addItem,
    updateItem,
    deleteItem,
    refreshData: loadData,
  };
};
